import { useState, useRef } from 'react';
import { Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ImageUploader({ images = [], onChange, maxImages = 10, label = 'Fotos del Producto' }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = maxImages - images.length;
    if (remaining <= 0) {
      toast.error(`Máximo ${maxImages} imágenes`);
      return;
    }

    const validFiles = files.slice(0, remaining).filter(f => {
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(f.type)) {
        toast.error(`${f.name}: Solo JPG y PNG`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name}: Máximo 10MB`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setUploading(true);
    const newUrls = [];

    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newUrls.push(res.data.url);
      } catch (err) {
        toast.error(`Error subiendo ${file.name}`);
      }
    }

    if (newUrls.length) {
      onChange([...images, ...newUrls]);
      toast.success(`${newUrls.length} imagen(es) subida(s)`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  const getFullUrl = (url) => {
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  return (
    <div className="space-y-3" data-testid="image-uploader">
      <label className="text-sm text-slate-400 uppercase tracking-wider">{label}</label>

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {images.map((url, index) => (
            <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-white/10">
              <img
                src={getFullUrl(url)}
                alt={`Foto ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23334155" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="12">Error</text></svg>'; }}
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${index}`}
              >
                <X className="w-3 h-3 text-white" />
              </button>
              {index === 0 && (
                <span className="absolute bottom-1 left-1 text-[10px] bg-cyan-500/80 text-white px-1.5 py-0.5 rounded">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {images.length < maxImages && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full border-2 border-dashed border-white/20 rounded-xl p-6 flex flex-col items-center justify-center gap-2 hover:border-cyan-500/50 hover:bg-white/5 transition-all cursor-pointer disabled:opacity-50"
          data-testid="upload-images-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              <span className="text-sm text-slate-400">Subiendo...</span>
            </>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-500" />
              <span className="text-sm text-slate-400">
                Subir fotos (JPG, PNG)
              </span>
              <span className="text-xs text-slate-600">
                {images.length}/{maxImages} fotos - Máx 10MB c/u
              </span>
            </>
          )}
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />
    </div>
  );
}
